import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { Toaster } from "@/components/ui/sonner";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ChatWidget from "@/components/ChatWidget";
import PromoBanner from "@/components/PromoBanner";
import StickyBookBar from "@/components/StickyBookBar";
import Home from "@/pages/Home";
import Fleet from "@/pages/Fleet";
import VehicleDetail from "@/pages/VehicleDetail";
import Gallery from "@/pages/Gallery";
import About from "@/pages/About";
import Policy from "@/pages/Policy";
import Contact from "@/pages/Contact";
import Payments from "@/pages/Payments";
import Extend from "@/pages/Extend";
import Upgrade from "@/pages/Upgrade";
import PayCharges from "@/pages/PayCharges";
import CheckIn from "@/pages/CheckIn";
import RentalReturn from "@/pages/RentalReturn";
import AdminScan from "@/pages/AdminScan";
import AuthCallback from "@/pages/AuthCallback";
import Booking from "@/pages/Booking";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Contract from "@/pages/Contract";
import QuoteAccept from "@/pages/QuoteAccept";
import GetQuote from "@/pages/GetQuote";
import Receipt from "@/pages/Receipt";
import Login from "@/pages/Login";
import AdminLogin from "@/pages/AdminLogin";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Account from "@/pages/Account";
import LoyaltyCard from "@/pages/LoyaltyCard";
import Loyalty from "@/pages/Loyalty";
import Admin from "@/pages/Admin";
import Terms from "@/pages/Terms";
import Review from "@/pages/Review";
import VerifyEmail from "@/pages/VerifyEmail";
import GuideDriving from "@/pages/GuideDriving";
import FAQ from "@/pages/FAQ";
import WinterSpecial from "@/pages/WinterSpecial";
import GuideBridgeToll from "@/pages/GuideBridgeToll";
import Flyer from "@/pages/Flyer";
import ManageBooking from "@/pages/ManageBooking";
import MyRentals from "@/pages/MyRentals";

function AppRoutes() {
  const location = useLocation();
  // OAuth callback: process session_id FIRST, before any route/auth checks
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <motion.div
      key={location.pathname}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
    >
      <Routes location={location}>
        <Route path="/" element={<Home />} />
        <Route path="/fleet" element={<Fleet />} />
        <Route path="/fleet/:vehicleId" element={<VehicleDetail />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/about" element={<About />} />
        <Route path="/policies" element={<Policy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/payments" element={<Payments />} />
        <Route path="/extend" element={<Extend />} />
        <Route path="/extend/:bookingId" element={<Extend />} />
        <Route path="/upgrade" element={<Upgrade />} />
        <Route path="/upgrade/:bookingId" element={<Upgrade />} />
        <Route path="/pay-charges/:bookingId" element={<PayCharges />} />
        <Route path="/check-in" element={<CheckIn />} />
        <Route path="/rental-return" element={<RentalReturn />} />
        <Route path="/book/:vehicleId" element={<Booking />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/contract/:bookingId" element={<Contract />} />
        <Route path="/quote/:token" element={<QuoteAccept />} />
        <Route path="/get-quote" element={<GetQuote />} />
        <Route path="/receipt/:bookingId" element={<Receipt />} />
        <Route path="/review/:bookingId" element={<Review />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/admin-login" element={<AdminLogin />} />
        <Route path="/register" element={<Register />} />
        <Route path="/account" element={<Account />} />
        <Route path="/loyalty" element={<Loyalty />} />
        <Route path="/guides/driving-in-nassau" element={<GuideDriving />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/winter-special" element={<WinterSpecial />} />
        <Route path="/guides/paradise-island-bridge-toll" element={<GuideBridgeToll />} />
        <Route path="/flyer" element={<Flyer />} />
        <Route path="/booking/:bookingId/manage" element={<ManageBooking />} />
        <Route path="/my-rentals" element={<MyRentals />} />
        <Route path="/loyalty-card" element={<LoyaltyCard />} />
        <Route path="/admin/scan" element={<AdminScan />} />
        <Route path="/admin/scan/:bookingId" element={<AdminScan />} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
    </motion.div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CurrencyProvider>
          <div className="min-h-screen bg-background text-foreground flex flex-col">
            <Navbar />
            <PromoBanner />
            <main className="flex-1 pb-20 md:pb-0">
              <AppRoutes />
            </main>
            <Footer />
            <ChatWidget />
            <StickyBookBar />
            <Toaster theme="light" position="top-center" richColors />
          </div>
        </CurrencyProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
